'use client';

// SidebarProgressSection - Compact progress/error indicators for the desktop left sidebar
// Shows transient upload/extraction state above the persisted DB sources list

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslations } from 'next-intl';
import { UrlCrawlProgress } from './UrlCrawlProgress';
import type { UploadedFile } from './FilePreviewList';
import type { UploadedImage } from './ImagePreview';
import type { ExtractedUrl } from './UrlPreviewCard';
import type { GoogleDocContent } from './GoogleDocPreviewCard';
import type { ExtractionSummary } from '../../hooks/useUrlExtraction';

export interface SidebarProgressSectionProps {
  /** Files currently being uploaded */
  files: UploadedFile[];
  /** Whether files are uploading */
  isUploadingFiles: boolean;
  /** Callback to remove a file */
  onRemoveFile: (fileId: string) => void;
  /** Images currently being uploaded/processed */
  images: UploadedImage[];
  /** Whether images are uploading */
  isUploadingImages: boolean;
  /** Whether images are being processed (vision AI) */
  isProcessingImages: boolean;
  /** Callback to remove an image */
  onRemoveImage: (imageId: string) => void;
  /** Whether URL extraction is in progress */
  isExtracting: boolean;
  /** Current count of URLs being extracted */
  currentCount: number;
  /** Total count of URLs expected */
  totalCount: number;
  /** Extraction summary (when complete) */
  extractionSummary?: ExtractionSummary | null;
  /** URLs with errors */
  urlErrors: ExtractedUrl[];
  /** Callback to remove a URL */
  onRemoveUrl?: (urlId: string) => void;
  /** Google docs with errors */
  googleDocErrors: GoogleDocContent[];
  /** Callback to remove a Google doc */
  onRemoveGoogleDoc?: (docId: string) => void;
}

interface ErrorItem {
  id: string;
  name: string;
  error: string;
  onRemove: () => void;
}

export function SidebarProgressSection({
  files,
  isUploadingFiles,
  onRemoveFile,
  images,
  isUploadingImages,
  isProcessingImages,
  onRemoveImage,
  isExtracting,
  currentCount,
  totalCount,
  extractionSummary,
  urlErrors,
  onRemoveUrl,
  googleDocErrors,
  onRemoveGoogleDoc,
}: SidebarProgressSectionProps) {
  const t = useTranslations('creator.contentSources.sidebar');
  const tFileUpload = useTranslations('creator.fileUpload');
  const tImageUpload = useTranslations('creator.imageUpload');
  const tUrlExtraction = useTranslations('creator.urlExtraction');
  const tGoogleExtraction = useTranslations('creator.googleExtraction');

  // Count items currently in-progress (not ready, not error)
  const inProgressCount = useMemo(() => {
    const filesInProgress = files.filter(f => f.status !== 'ready' && f.status !== 'error').length;
    const imagesInProgress = images.filter(i => i.status !== 'ready' && i.status !== 'error').length;
    return filesInProgress + imagesInProgress;
  }, [files, images]);

  const isAnyProcessing = isUploadingFiles || isUploadingImages || isProcessingImages || inProgressCount > 0;

  // Collect all error items into a uniform list
  const errorItems = useMemo((): ErrorItem[] => {
    const items: ErrorItem[] = [];

    for (const file of files) {
      if (file.status === 'error') {
        items.push({
          id: `file-${file.id}`,
          name: file.fileName,
          error: tFileUpload(`error.${file.errorMessage || 'extractionFailed'}`),
          onRemove: () => onRemoveFile(file.id),
        });
      }
    }

    for (const image of images) {
      if (image.status === 'error') {
        items.push({
          id: `image-${image.id}`,
          name: image.fileName,
          error: tImageUpload(`error.${image.errorMessage || 'uploadFailed'}`),
          onRemove: () => onRemoveImage(image.id),
        });
      }
    }

    for (const url of urlErrors) {
      items.push({
        id: `url-${url.id}`,
        name: url.url,
        error: tUrlExtraction(`error.${url.errorCode || 'extractionFailed'}`),
        onRemove: () => onRemoveUrl?.(url.id),
      });
    }

    for (const doc of googleDocErrors) {
      items.push({
        id: `gdoc-${doc.id}`,
        name: doc.title || doc.url,
        error: tGoogleExtraction(`error.${doc.errorCode || 'extractionFailed'}`),
        onRemove: () => onRemoveGoogleDoc?.(doc.id),
      });
    }

    return items;
  }, [files, images, urlErrors, googleDocErrors, onRemoveFile, onRemoveImage, onRemoveUrl, onRemoveGoogleDoc, tFileUpload, tImageUpload, tUrlExtraction, tGoogleExtraction]);

  // Return null if nothing to show
  const hasUrlCrawl = isExtracting || (extractionSummary && extractionSummary.linksDiscovered > 0);
  if (!isAnyProcessing && !hasUrlCrawl && errorItems.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Upload/processing activity indicator */}
      {isAnyProcessing && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            mb: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('processingItems', { count: inProgressCount || 1 })}
          </Typography>
        </Box>
      )}

      {/* URL crawl progress bar - reuse existing component */}
      <UrlCrawlProgress
        isExtracting={isExtracting}
        currentCount={currentCount}
        totalCount={totalCount}
        summary={extractionSummary ?? null}
      />

      {/* Error items - compact list of failed uploads/extractions */}
      {errorItems.length > 0 && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="caption" fontWeight="medium">
            {t('failedItems', { count: errorItems.length })}
          </Typography>
          <List dense disablePadding>
            {errorItems.map((item) => (
              <ListItem
                key={item.id}
                sx={{ py: 0.25, px: 0 }}
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={item.onRemove}
                    sx={{ p: 0.25 }}
                    aria-label="dismiss"
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="caption" noWrap sx={{ maxWidth: '80%' }}>
                      {item.name}
                    </Typography>
                  }
                  secondary={
                    item.error ? (
                      <Typography variant="caption" color="error.dark">
                        {item.error}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </Box>
  );
}

export default SidebarProgressSection;
